import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { getRegionZone } from '@helpers/geo-location'
import BaseCollectionTileContext from './BaseCollectionTileContext'
import ImageGallery from './compositions/ImageGallery'
import Title from './compositions/Title'
import Description from './compositions/Description'
import PriceRange from './compositions/PriceRange'
import RoomOptions from './compositions/RoomOptions'
import Swatches from './compositions/Swatches'
import StyleSwiper from './compositions/StyleSwiper'
import ShopButton from './compositions/ShopButton'
import AnchorWrapper from './compositions/AnchorWrapper'
import { determineSwatchData } from './helpers'

export const BaseCollectionTile = ({ collection, children }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [activeColor, setActiveColor] = useState()
  const [slideToIndex, setSlideToIndex] = useState(0)

  const { zone: priceZone } = getRegionZone()

  const swatchData = useMemo(() => {
    if (!collection?.variations) return []

    return determineSwatchData(collection.variations)
  }, [collection])

  // This is used to make sure the active index updates the active color via its range
  useEffect(() => {
    let foundColor
    let swatchIndex = 0
    while (!foundColor && swatchIndex < swatchData.length) {
      const swatch = swatchData[swatchIndex]
      if (activeIndex >= swatch.startIndex && activeIndex < swatch.startIndex + swatch.length) {
        foundColor = swatch
      }
      swatchIndex += 1
    }
    if (foundColor) {
      setActiveColor(foundColor.label)
    }
  }, [activeIndex, swatchData])

  // Clicking a color slides to an index.  After we slide to an index, we need to set it back to null.
  // This is because a user could swipe out of the current color, click the swatch, and then not be directed to the correct color.
  useEffect(() => {
    if (typeof slideToIndex === 'number') {
      setSlideToIndex(null)
    }
  }, [slideToIndex])

  // Tracks the active index of the image gallery.
  // This is a strategy to get BaseSwiper's active index from a parent component.
  const activeIndexTracker = index => {
    setActiveIndex(index)
  }

  // When a color is clicked, we set the selected color and slide to the starting index of the color
  const handleColorClick = color => {
    setActiveColor(color)
    const colorIndex = swatchData.find(swatch => swatch.label === color)?.startIndex
    if (typeof colorIndex === 'number') {
      setSlideToIndex(colorIndex)
    }
  }

  return (
    <BaseCollectionTileContext.Provider
      value={{
        collection,
        priceZone,
        activeIndex,
        activeColor,
        slideToIndex,
        activeIndexTracker,
        handleColorClick,
        swatchData,
      }}
    >
      {children}
    </BaseCollectionTileContext.Provider>
  )
}

BaseCollectionTile.propTypes = {
  collection: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
}

export default BaseCollectionTile

BaseCollectionTile.ImageGallery = ImageGallery
BaseCollectionTile.Title = Title
BaseCollectionTile.Description = Description
BaseCollectionTile.PriceRange = PriceRange
BaseCollectionTile.RoomOptions = RoomOptions
BaseCollectionTile.Swatches = Swatches
BaseCollectionTile.StyleSwiper = StyleSwiper
BaseCollectionTile.ShopButton = ShopButton
BaseCollectionTile.AnchorWrapper = AnchorWrapper
